import config from "@/libs/config";
import { useCallback, useContext, useState, useMemo, useEffect } from "react";
import { useRouter } from "next/router";
import { useForm, FormProvider } from "react-hook-form";
import { css } from "@emotion/react";
import type Rollbar from "rollbar";
import { MainTemplate } from "@/components/templates";
import { useSnackBar, Font } from "@moneyforward/mfx_biz_react_components";
import {
  VerifyProvisionalRegistrationTokenWithConnectBoardMutation,
  useRegisterVerifiedCompanyWithConnectBoardMutation,
  useVerifyProvisionalRegistrationTokenWithConnectBoardMutation,
} from "@/generated/graphql";
import { ConnectBoardRegisterCompanyForm } from "@/components/organisms";
import { AuthContext, SessionInfo } from "@/contexts/AuthContext";
import { PageTitle } from "@/components/molecules";
import { ErrorCode, errorCodeIsIncluded, moveToErrorPage } from "@/libs/errors";
import { ApolloError } from "@apollo/client";
import { setLoginSource, LoginSource } from "@/libs/loginSource";
import { useRegistrationRedirectParams } from "@/components/hooks/useRegistrationRedirectParams";
import useRollbarUtil from "../hooks/useRollbarUtil";

type FormValues = {
  lastName: string;
  lastNameKana: string;
  middleName: string;
  middleNameKana: string;
  firstName: string;
  firstNameKana: string;
  email?: string;
  // password?: string;
  companyName: string;
};

export const ConnectBoardRegisterCompanyPage = () => {
  const { rollbar } = useRollbarUtil();
  const authContext = useContext(AuthContext);
  const { notify } = useSnackBar();
  const [verifying, setVerifying] = useState(true);
  const [email, setEmail] = useState("");
  const [registrationCode, setRegistrationCode] = useState("");

  const router = useRouter();
  const { app } = router.query;
  const token = useMemo(() => {
    return router.query.token as string;
  }, [router.query.token]);

  const methods = useForm<FormValues>({
    reValidateMode: "onBlur",
    defaultValues: {
      companyName: "",
      middleName: "",
      middleNameKana: "",
    },
  });

  const signIn = async (token: {
    access_token: string;
    id_token: string;
    refresh_token: string;
    token_type: string;
  }) => {
    try {
      const session: SessionInfo = {
        accessToken: token.access_token,
        idToken: token.id_token,
        refreshToken: token.refresh_token,
      };
      await authContext.setSignInUserSession(session);
      setLoginSource(LoginSource.connectboard);
      // eslint-disable-next-line react-hooks/rules-of-hooks -- TODO: fix to comply with the rules of react-hooks as not following them may lead to unintended side effects
      useRegistrationRedirectParams(router, app);
    } catch (err) {
      rollbar.error("Failed to sign in", err);
      notify("ログインに失敗しました");
      setLoginSource(LoginSource.none);
      moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
    }
  };

  const [verifyProvisionalRegistrationToken] =
    useVerifyProvisionalRegistrationTokenWithConnectBoardMutation({
      onCompleted: (
        data: VerifyProvisionalRegistrationTokenWithConnectBoardMutation
      ) => {
        if (!data.verifyProvisionalRegistrationTokenWithConnectBoard.result) {
          moveToErrorPage(ErrorCode.INVALID_EMAIL_AUTH_TOKEN);
          return;
        }
        setVerifying(false);
        setEmail(data.verifyProvisionalRegistrationTokenWithConnectBoard.email);
        setRegistrationCode(
          data.verifyProvisionalRegistrationTokenWithConnectBoard
            .registrationCode
        );
      },
      onError: (err) => {
        if (errorCodeIsIncluded(err, ErrorCode.INVALID_EMAIL_AUTH_TOKEN)) {
          moveToErrorPage(ErrorCode.INVALID_EMAIL_AUTH_TOKEN);
          return;
        }
        rollbar.error(
          "Failed to verify provisional registration token with connect board",
          err
        );
        moveToErrorPage();
      },
    });

  const [registerNewVerifiedCompany] =
    useRegisterVerifiedCompanyWithConnectBoardMutation({
      onCompleted: (data) => {
        const authToken = data.registerVerifiedCompanyWithConnectBoard.token;
        if (!token) {
          moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
          return;
        }
        signIn(authToken);
      },
      onError: (err) => {
        handleError(
          rollbar,
          "Failed to register verified company with connect board"
        )(err);
      },
    });

  useEffect(() => {
    if (!token) return;
    verifyProvisionalRegistrationToken({
      variables: { input: { token } },
    });
  }, [token]);

  const handleSubmit = useCallback(
    async (v: FormValues) => {
      const client_id = config.COGNITO_CLIENT_ID;
      await registerNewVerifiedCompany({
        variables: {
          input: {
            client_id,
            registrationCode,
            email,
            ...v,
          },
        },
      });
    },
    [registerNewVerifiedCompany, registrationCode, email]
  );

  if (!router.isReady) {
    return <div></div>;
  }

  return (
    <MainTemplate title={`新規登録`} disableAccountDropDown={true}>
      {verifying ? (
        <div css={messageWrapper}>
          <Font variant="m" component="p">
            確認中...
          </Font>
        </div>
      ) : (
        <>
          <PageTitle
            mainTitle={`新規登録`}
            subTitle={
              <>
                ユーザー情報を入力して利用規約に同意し
                <br />
                登録を開始してください
              </>
            }
          />
          <FormProvider {...methods}>
            <ConnectBoardRegisterCompanyForm
              onSubmit={handleSubmit}
              email={email}
            />
          </FormProvider>
        </>
      )}
    </MainTemplate>
  );
};

const handleError =
  (rollbar: Rollbar, messageToRollbar: string) => (err: ApolloError) => {
    const errorList: Array<ErrorCode> = [
      ErrorCode.CONNBOARD_USER_IS_NOT_ALLOWED_TO_REGISTER_COMPANY,
      ErrorCode.CONNBOARD_COMPANY_ALREADY_REGISTERED,
      ErrorCode.CONNBOARD_INVALID_REGISTRATION_TOKEN,
    ];

    errorList.forEach((code) => {
      if (errorCodeIsIncluded(err, code)) {
        moveToErrorPage(code);
        return;
      }
    });

    // 上記にないエラーでもconnectboard ボタンのあるエラーページに遷移する
    // username はランダム発番するため、USERNAME_EXISTS はInternalError として扱う
    rollbar.error(messageToRollbar, err);
    moveToErrorPage(ErrorCode.CONNBOARD_INTERNAL_SERVER_ERROR);
  };

const messageWrapper = css({
  display: "flex",
  justifyContent: "center",
});
